import { CalendarDays, MapPin, Sparkles, HeartHandshake, UtensilsCrossed, Leaf, Users, Briefcase } from "lucide-react";
import { PageHero, CTABand, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const SERVICES = [
  { icon: Sparkles, title: "Faith-Based Empowerment Circles", desc: "Weekly gatherings of scripture, prayer, testimony, and encouragement." },
  { icon: HeartHandshake, title: "Holistic Health Coaching", desc: "One-on-one wellness coaching for mind, body, and spirit." },
  { icon: UtensilsCrossed, title: "Nourishment & Cooking Workshops", desc: "Shared meals and hands-on cooking classes using garden-fresh produce." },
  { icon: Leaf, title: "Garden Training Days", desc: "Seasonal, hands-in-the-soil training in growing food sustainably." },
  { icon: Briefcase, title: "Vocational & Life-Skills Training", desc: "Resume building, interview preparation, budgeting, and career pathways." },
  { icon: Users, title: "Community Support Circles", desc: "Safe, trauma-informed spaces where women are heard and held." },
];

const EVENTS = [
  { id: "nourish-dinner", title: "Nourish & Nourish Community Dinner", date: "Third Wednesday of each month", place: "Garden Grub Kitchen" },
  { id: "hope-circle", title: "Circle of Hope Worship & Prayer Night", date: "Coming soon", place: "Details announced by email", soon: true },
  { id: "skills-workshop", title: "Vocational Skills Open Workshop", date: "Quarterly — next session announced by email", place: "Garden Grub Learning Space" },
];

export default function ServicesEvents() {
  useTitle("Services & Events | Bee Courageous @ Garden Grub");

  return (
    <>
      <PageHero
        eyebrow="Services & Events"
        lines={["Pull up a chair.", "Grab a garden glove."]}
        lede="From coaching to cooking classes to garden workdays, everything we offer is an open door into healing community."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <SectionHead eyebrow="Our Services" title="Care for the whole woman" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.06 * i}>
              <article className="card-soft h-full" data-testid={`service-${i}`}>
                <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-garden-soft text-garden">
                  <s.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="font-serif text-xl font-semibold text-charcoal">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{s.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-wing-soft/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHead
            center
            eyebrow="Upcoming Rhythms"
            title="Events & gatherings"
            text="Every gathering is free and every woman is welcome — join the email list below or contact us to attend."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3 lg:gap-8">
            {EVENTS.map((ev, i) => (
              <Reveal key={ev.id} delay={0.07 * i}>
                <article className="card-soft h-full" data-testid={`event-${ev.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-xl font-semibold text-wing sm:text-2xl">{ev.title}</h3>
                    {ev.soon && (
                      <span className="shrink-0 rounded-full bg-pollen-soft px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-pollen-dark" data-testid="event-soon-badge">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="mt-3 flex items-center gap-2 text-sm text-charcoal/70">
                    <CalendarDays className="h-4 w-4 text-pollen-dark" aria-hidden="true" /> {ev.date}
                  </p>
                  <p className="mt-1.5 flex items-center gap-2 text-sm text-charcoal/70">
                    <MapPin className="h-4 w-4 text-garden" aria-hidden="true" /> {ev.place}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Not sure where to begin? Start with a meal."
        text="Come hungry, leave hopeful. Our community dinners are the gentlest first step."
        primaryLabel="Contact Us"
        primaryTo="/contact"
      />
    </>
  );
}
